import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Clock, CheckCircle, XCircle, Briefcase, MapPin, Building2, 
  Calendar, Loader2, Eye, FileText, AlertTriangle, Plus, Edit, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

// Options pour les selects
const TYPES_CONTRAT = ['CDI', 'CDD', 'Intérim', 'Alternance', 'Stage', 'Contrat Pro', 'Autre'];
const SECTEURS_ACTIVITE = [
  'Agriculture', 'Agroalimentaire', 'BTP', 'Commerce/Distribution', 
  'Hôtellerie/Restauration', 'Industrie', 'Logistique/Transport',
  'Santé/Social', 'Services aux entreprises', 'Services à la personne',
  'Tourisme', 'Autre'
];
const ZONES = ['Saint-Pierre', 'Sainte-Marie'];

const MesOffresEmploi = () => {
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Formulaire d'édition/création
  const [editForm, setEditForm] = useState({
    intitule_poste: '',
    type_contrat: '',
    secteur_activite: '',
    lieu_mission: '',
    zone_geographique: '',
    description: '',
    competences_requises: '',
    salaire: '',
    horaires: ''
  });

  const resetForm = () => {
    setEditForm({
      intitule_poste: '',
      type_contrat: '',
      secteur_activite: '',
      lieu_mission: '',
      zone_geographique: '',
      description: '',
      competences_requises: '',
      salaire: '',
      horaires: ''
    });
  };

  useEffect(() => {
    loadMesOffres();
  }, []);

  const loadMesOffres = async () => {
    try {
      const response = await api.get('/bourse-emploi/mes-offres');
      setOffres(response.data || []);
    } catch (error) {
      console.error('Erreur chargement mes offres:', error);
      toast.error('Erreur de chargement de vos offres');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleCreateOffre = async () => {
    // Validation basique
    if (!editForm.intitule_poste || !editForm.type_contrat || !editForm.secteur_activite || 
        !editForm.lieu_mission || !editForm.zone_geographique) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/offres-emploi', editForm);
      toast.success(response.data?.message || 'Offre soumise avec succès !');
      setShowCreateDialog(false);
      resetForm();
      loadMesOffres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (offre) => {
    setSelectedOffre(offre);
    setEditForm({
      intitule_poste: offre.intitule_poste || '',
      type_contrat: offre.type_contrat || '',
      secteur_activite: offre.secteur_activite || '',
      lieu_mission: offre.lieu_mission || '',
      zone_geographique: offre.zone_geographique || '',
      description: offre.description || '',
      competences_requises: offre.competences_requises || '',
      salaire: offre.salaire || '',
      horaires: offre.horaires || ''
    });
    setShowEditDialog(true);
    setShowDetailDialog(false);
  };

  const handleResoumettre = async () => {
    if (!selectedOffre) return;
    
    // Validation basique
    if (!editForm.intitule_poste || !editForm.type_contrat || !editForm.secteur_activite || 
        !editForm.lieu_mission || !editForm.zone_geographique) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.put(`/offres-emploi/${selectedOffre.id}/resoumettre`, editForm);
      toast.success(response.data?.message || 'Offre resoumise avec succès !');
      setShowEditDialog(false);
      loadMesOffres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la resoumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'validee':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Validée
          </Badge>
        );
      case 'en_attente':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <Clock className="w-3 h-3 mr-1" />
            En attente
          </Badge>
        );
      case 'refusee':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200">
            <XCircle className="w-3 h-3 mr-1" />
            Refusée
          </Badge>
        );
      default:
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Publiée
          </Badge>
        );
    }
  };

  const getStatutDescription = (offre) => {
    switch (offre.statut_validation) {
      case 'validee':
        return offre.validee_par_nom 
          ? `Validée par ${offre.validee_par_nom} le ${new Date(offre.date_validation).toLocaleDateString('fr-FR')}`
          : 'Offre publiée et visible par les bénéficiaires';
      case 'en_attente':
        return 'En attente de validation par un administrateur';
      case 'refusee':
        return offre.motif_refus 
          ? `Motif : ${offre.motif_refus}`
          : 'Offre non validée par l\'administrateur';
      default:
        return 'Offre publiée';
    }
  };

  // Compter les offres par statut
  const stats = {
    total: offres.length,
    validees: offres.filter(o => o.statut_validation === 'validee' || !o.statut_validation).length,
    en_attente: offres.filter(o => o.statut_validation === 'en_attente').length,
    refusees: offres.filter(o => o.statut_validation === 'refusee').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 rounded-3xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-bold flex items-center gap-2 text-blue-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <FileText className="w-6 h-6" />
                Mes Offres Soumises
              </CardTitle>
              <p className="text-blue-700 text-sm mt-1">
                Suivez le statut de vos offres d'emploi soumises à validation.
              </p>
            </div>
            <Button 
              onClick={openCreateDialog}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Proposer une offre
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <p className="text-3xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm border-l-4 border-emerald-500">
              <p className="text-3xl font-bold text-emerald-600">{stats.validees}</p>
              <p className="text-sm text-slate-500">Validées</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm border-l-4 border-amber-500">
              <p className="text-3xl font-bold text-amber-600">{stats.en_attente}</p>
              <p className="text-sm text-slate-500">En attente</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm border-l-4 border-red-500">
              <p className="text-3xl font-bold text-red-600">{stats.refusees}</p>
              <p className="text-sm text-slate-500">Refusées</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des offres */}
      {offres.length === 0 ? (
        <Card className="bg-white rounded-3xl border-slate-200">
          <CardContent className="p-8 text-center">
            <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">Vous n'avez pas encore soumis d'offre</p>
            <p className="text-sm text-slate-400 mt-1 mb-4">
              Cliquez sur "Proposer une offre" pour commencer.
            </p>
            <Button onClick={openCreateDialog} className="rounded-xl bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Proposer une offre
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {offres.map((offre) => (
            <Card 
              key={offre.id} 
              className={`bg-white rounded-3xl shadow-sm hover:shadow-md transition-shadow ${
                offre.statut_validation === 'refusee' ? 'border-red-200' :
                offre.statut_validation === 'en_attente' ? 'border-amber-200' :
                'border-emerald-200'
              }`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Badges */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {getStatutBadge(offre.statut_validation)}
                      <Badge className={`${
                        offre.type_contrat === 'CDI' ? 'bg-emerald-100 text-emerald-700' :
                        offre.type_contrat === 'CDD' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {offre.type_contrat}
                      </Badge>
                      <Badge className="bg-purple-100 text-purple-700">
                        {offre.secteur_activite}
                      </Badge>
                    </div>
                    
                    {/* Titre */}
                    <h3 className="font-bold text-lg text-slate-900 mb-1">{offre.intitule_poste}</h3>
                    
                    {/* Entreprise */}
                    {offre.entreprise_nom && (
                      <p className="text-slate-600 flex items-center gap-1 mb-2 text-sm">
                        <Building2 className="w-4 h-4" />
                        {offre.entreprise_nom}
                      </p>
                    )}
                    
                    {/* Infos */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {offre.lieu_mission} ({offre.zone_geographique})
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Soumis le {new Date(offre.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    
                    {/* Description du statut */}
                    <div className={`text-sm p-3 rounded-xl ${
                      offre.statut_validation === 'refusee' ? 'bg-red-50 text-red-700' :
                      offre.statut_validation === 'en_attente' ? 'bg-amber-50 text-amber-700' :
                      'bg-emerald-50 text-emerald-700'
                    }`}>
                      {offre.statut_validation === 'refusee' && (
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                      )}
                      {getStatutDescription(offre)}
                    </div>
                  </div>
                  
                  {/* Boutons actions */}
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setSelectedOffre(offre); setShowDetailDialog(true); }}
                      className="rounded-xl"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Détails
                    </Button>
                    
                    {/* Bouton Modifier / Resoumettre pour les offres refusées */}
                    {offre.statut_validation === 'refusee' && (
                      <Button 
                        size="sm"
                        onClick={() => openEditDialog(offre)}
                        className="rounded-xl bg-blue-600 hover:bg-blue-700"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Resoumettre
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Détail Offre */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          {selectedOffre && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {getStatutBadge(selectedOffre.statut_validation)}
                  <Badge className={`${
                    selectedOffre.type_contrat === 'CDI' ? 'bg-emerald-100 text-emerald-700' :
                    selectedOffre.type_contrat === 'CDD' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {selectedOffre.type_contrat}
                  </Badge>
                </div>
                <DialogTitle className="text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {selectedOffre.intitule_poste}
                </DialogTitle>
                <DialogDescription>
                  Soumis le {new Date(selectedOffre.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Statut détaillé */}
                <div className={`p-4 rounded-xl ${
                  selectedOffre.statut_validation === 'refusee' ? 'bg-red-50 border border-red-200' :
                  selectedOffre.statut_validation === 'en_attente' ? 'bg-amber-50 border border-amber-200' :
                  'bg-emerald-50 border border-emerald-200'
                }`}>
                  <h4 className={`font-semibold mb-2 ${
                    selectedOffre.statut_validation === 'refusee' ? 'text-red-800' :
                    selectedOffre.statut_validation === 'en_attente' ? 'text-amber-800' :
                    'text-emerald-800'
                  }`}>
                    {selectedOffre.statut_validation === 'validee' ? 'Offre Validée' :
                     selectedOffre.statut_validation === 'en_attente' ? 'En Attente de Validation' :
                     selectedOffre.statut_validation === 'refusee' ? 'Offre Refusée' : 'Offre Publiée'}
                  </h4>
                  <p className={`text-sm ${
                    selectedOffre.statut_validation === 'refusee' ? 'text-red-700' :
                    selectedOffre.statut_validation === 'en_attente' ? 'text-amber-700' :
                    'text-emerald-700'
                  }`}>
                    {getStatutDescription(selectedOffre)}
                  </p>
                  {selectedOffre.validee_par_nom && selectedOffre.statut_validation !== 'en_attente' && (
                    <p className="text-xs text-slate-500 mt-2">
                      Traité par {selectedOffre.validee_par_nom} le {new Date(selectedOffre.date_validation).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>

                {/* Infos principales */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="text-slate-500 text-sm">Type de contrat</p>
                    <p className="font-medium">{selectedOffre.type_contrat}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Secteur</p>
                    <p className="font-medium">{selectedOffre.secteur_activite}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Lieu</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      {selectedOffre.lieu_mission} ({selectedOffre.zone_geographique})
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Entreprise</p>
                    <p className="font-medium">{selectedOffre.entreprise_nom || 'Non spécifiée'}</p>
                  </div>
                </div>

                {/* Description */}
                {selectedOffre.description && (
                  <div>
                    <p className="text-slate-700 font-semibold mb-2">Description</p>
                    <div className="p-4 bg-white border rounded-xl whitespace-pre-wrap text-sm text-slate-600">
                      {selectedOffre.description}
                    </div>
                  </div>
                )}

                {/* Compétences */}
                {selectedOffre.competences_requises && (
                  <div>
                    <p className="text-slate-700 font-semibold mb-2">Compétences requises</p>
                    <div className="p-4 bg-white border rounded-xl whitespace-pre-wrap text-sm text-slate-600">
                      {selectedOffre.competences_requises}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                {selectedOffre.statut_validation === 'refusee' && (
                  <Button 
                    onClick={() => openEditDialog(selectedOffre)}
                    className="rounded-xl bg-blue-600 hover:bg-blue-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Modifier et resoumettre
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-xl">
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Modification / Resoumission */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Edit className="w-5 h-5 text-blue-600" />
              Modifier et resoumettre l'offre
            </DialogTitle>
            <DialogDescription>
              Corrigez les informations demandées puis resoumettez pour validation.
            </DialogDescription>
          </DialogHeader>

          {selectedOffre?.motif_refus && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-amber-800 font-semibold text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Motif du refus précédent :
              </p>
              <p className="text-amber-700 text-sm mt-1">{selectedOffre.motif_refus}</p>
            </div>
          )}

          <div className="space-y-4 py-4">
            {/* Intitulé */}
            <div>
              <Label className="text-slate-700">Intitulé du poste *</Label>
              <Input
                value={editForm.intitule_poste}
                onChange={(e) => setEditForm({...editForm, intitule_poste: e.target.value})}
                placeholder="Ex: Vendeur polyvalent"
                className="mt-1 rounded-xl"
              />
            </div>

            {/* Type de contrat et Secteur */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Type de contrat *</Label>
                <Select 
                  value={editForm.type_contrat} 
                  onValueChange={(v) => setEditForm({...editForm, type_contrat: v})}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_CONTRAT.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Secteur d'activité *</Label>
                <Select 
                  value={editForm.secteur_activite} 
                  onValueChange={(v) => setEditForm({...editForm, secteur_activite: v})}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTEURS_ACTIVITE.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lieu et Zone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Lieu de la mission *</Label>
                <Input
                  value={editForm.lieu_mission}
                  onChange={(e) => setEditForm({...editForm, lieu_mission: e.target.value})}
                  placeholder="Ex: Centre-ville"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Zone géographique *</Label>
                <Select 
                  value={editForm.zone_geographique} 
                  onValueChange={(v) => setEditForm({...editForm, zone_geographique: v})}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {ZONES.map(z => (
                      <SelectItem key={z} value={z}>{z}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Salaire et Horaires */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Salaire</Label>
                <Input
                  value={editForm.salaire}
                  onChange={(e) => setEditForm({...editForm, salaire: e.target.value})}
                  placeholder="Ex: SMIC, 1800€ brut..."
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Horaires</Label>
                <Input
                  value={editForm.horaires}
                  onChange={(e) => setEditForm({...editForm, horaires: e.target.value})}
                  placeholder="Ex: 9h-17h, 35h/semaine..."
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-slate-700">Description des missions</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                placeholder="Décrivez les missions principales du poste..."
                className="mt-1 rounded-xl"
                rows={4}
              />
            </div>

            {/* Compétences */}
            <div>
              <Label className="text-slate-700">Compétences / Pré-requis</Label>
              <Textarea
                value={editForm.competences_requises}
                onChange={(e) => setEditForm({...editForm, competences_requises: e.target.value})}
                placeholder="Listez les compétences et qualifications requises..."
                className="mt-1 rounded-xl"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)} 
              disabled={submitting}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleResoumettre}
              disabled={submitting}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Resoumettre pour validation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Création nouvelle offre */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Plus className="w-5 h-5 text-blue-600" />
              Proposer une nouvelle offre d'emploi
            </DialogTitle>
            <DialogDescription>
              Votre offre sera soumise à validation par un administrateur avant d'être publiée.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Intitulé */}
            <div>
              <Label className="text-slate-700">Intitulé du poste *</Label>
              <Input
                value={editForm.intitule_poste}
                onChange={(e) => setEditForm({...editForm, intitule_poste: e.target.value})}
                placeholder="Ex: Vendeur polyvalent, Cuisinier, Agent d'entretien..."
                className="mt-1 rounded-xl"
              />
            </div>

            {/* Type de contrat et Secteur */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Type de contrat *</Label>
                <Select 
                  value={editForm.type_contrat} 
                  onValueChange={(v) => setEditForm({...editForm, type_contrat: v})}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_CONTRAT.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Secteur d'activité *</Label>
                <Select 
                  value={editForm.secteur_activite} 
                  onValueChange={(v) => setEditForm({...editForm, secteur_activite: v})}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTEURS_ACTIVITE.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lieu et Zone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Lieu de la mission *</Label>
                <Input
                  value={editForm.lieu_mission}
                  onChange={(e) => setEditForm({...editForm, lieu_mission: e.target.value})}
                  placeholder="Ex: Centre-ville, Zone industrielle..."
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Zone géographique *</Label>
                <Select 
                  value={editForm.zone_geographique} 
                  onValueChange={(v) => setEditForm({...editForm, zone_geographique: v})}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {ZONES.map(z => (
                      <SelectItem key={z} value={z}>{z}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Salaire et Horaires */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Salaire</Label>
                <Input
                  value={editForm.salaire}
                  onChange={(e) => setEditForm({...editForm, salaire: e.target.value})}
                  placeholder="Ex: SMIC, 1800€ brut, À négocier..."
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Horaires</Label>
                <Input
                  value={editForm.horaires}
                  onChange={(e) => setEditForm({...editForm, horaires: e.target.value})}
                  placeholder="Ex: 9h-17h, 35h/semaine, Temps partiel..."
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-slate-700">Description des missions</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                placeholder="Décrivez les missions principales du poste..."
                className="mt-1 rounded-xl"
                rows={4}
              />
            </div>

            {/* Compétences */}
            <div>
              <Label className="text-slate-700">Compétences / Pré-requis</Label>
              <Textarea
                value={editForm.competences_requises}
                onChange={(e) => setEditForm({...editForm, competences_requises: e.target.value})}
                placeholder="Listez les compétences et qualifications requises..."
                className="mt-1 rounded-xl"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowCreateDialog(false); resetForm(); }} 
              disabled={submitting}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleCreateOffre}
              disabled={submitting}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Soumettre pour validation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MesOffresEmploi;
